import { Injectable } from '@angular/core';
import { Schedule_ItemType, Schedule, generateScheduleDaysFromRules } from '@cf-shared/data-model/schedule';
import { minBy, maxBy } from 'lodash-es';
import { DateTime } from 'luxon';
import { hoursToIsoTime } from '@cf-shared/time';
import { wrapIntegerToInterval } from '@cf-shared/shared-util';
import { LL } from 'i18n/i18n.service';
import { weekdayName } from 'util/formatting';

export class ScheduleUIItem {
  name: string;
  timeLabel?: string;
  icon?: string;
  type: Schedule_ItemType;
  hourId?: number;
  dayIndex?: number;
  timeISO?: string;
  nudgeY?: number;
}

export class ScheduleUIDay {
  index: number;
  headerText: string;
  items: ScheduleUIItem[];
  indexOffset: number;
}

/**
 * The stable identity of a schedule tip.
 *
 * Each value is the name of the tip's text under `LL.schedule.warnings`, so
 * `scheduleTipText` can look the sentence up from it. The id, not the sentence,
 * is what the warnings map is keyed by and what telemetry reports, so a reworded
 * or translated tip is still the same tip.
 */
export enum ScheduleTipId {
  MealBeforeWorkout = 'mealBeforeWorkout',
  MealAfterWorkout = 'mealAfterWorkout',
  MinTimeBetweenMeals = 'minTimeBetweenMeals',
  OneMealCloseToWo = 'oneMealCloseToWo',
  PrePostWoMealDistance = 'prePostWoMealDistance',
  ShortFeedingWindow = 'shortFeedingWindow',
  WorkoutTimingIrregular = 'workoutTimingIrregular',
  MealTimingIrregular = 'mealTimingIrregular',
  SleepTimingIrregular = 'sleepTimingIrregular',
  ShortSleep = 'shortSleep',
}

/** One place a tip applies: the day (or pair of days) and the items it is about. */
export interface ScheduleTipOccurrence {
  day?: ScheduleUIDay | [ScheduleUIDay, ScheduleUIDay];
  items?: ScheduleUIItem[];
}

export type ScheduleWarningsData = Partial<Record<ScheduleTipId, ScheduleTipOccurrence[]>>;

/** The localized sentence shown for a tip. */
export function scheduleTipText(id: ScheduleTipId): string {
  return LL.schedule.warnings[id]();
}

/** Records one occurrence of a tip. */
type PushTip = (id: ScheduleTipId, occurrence: ScheduleTipOccurrence) => void;

const hourDivisions = 60; /** How fine grained do we allow slots to be placed inside one hour. */

function hourIdToHour(hourId: number) {
  return hourId / hourDivisions;
}

function itemDistance(a: ScheduleUIItem, b: ScheduleUIItem): number {
  let timeA = hourIdToHour(a.hourId) + a.dayIndex * 24;
  let timeB = hourIdToHour(b.hourId) + b.dayIndex * 24;
  let delta = Math.abs(timeA - timeB);
  const hoursPerWeekDiv2 = (24 * 7) / 2;
  return (delta + hoursPerWeekDiv2) % hoursPerWeekDiv2; // this wraps around for sun-mon case
}

/** One day's time for one schedule slot (a meal number, the workout, wake-up or bedtime). */
interface SlotTime {
  day: ScheduleUIDay;
  hour: number;
}

/**
 * Lays hours of the day out so that times either side of midnight sit next to each other.
 *
 * The hours are points on a 24-hour clock. The shortest stretch of the clock that holds
 * all of them starts just after the biggest gap between two neighbouring times, counting
 * the gap that runs through midnight. Hours before that start get 24 added, so ordinary
 * max - min is the length of that stretch and an ordinary mean is a real average. For
 * example 23:00 and 00:30 become 23 and 24.5: 1.5 hours apart, not 22.5.
 * Returns the hours in the order given. Depends only on the set of hours, never their order.
 */
function unwrapHoursOnClock(hours: number[]): number[] {
  if (hours.length === 0) {
    return [];
  }
  const sorted = [...hours].sort((a, b) => a - b);
  // the gap through midnight comes first, so on a tie nothing is moved
  let start = sorted[0];
  let largestGap = sorted[0] + 24 - sorted[sorted.length - 1];
  for (let k = 1; k < sorted.length; k++) {
    const gap = sorted[k] - sorted[k - 1];
    if (gap > largestGap) {
      largestGap = gap;
      start = sorted[k];
    }
  }
  return hours.map(hour => hour < start ? hour + 24 : hour);
}

/** The day's meals, in time order. */
function mealsOf(day: ScheduleUIDay): ScheduleUIItem[] {
  return day.items.filter(item => item.type === Schedule_ItemType.Meal);
}

/** Checks the meals around the day's workout: one before it, one after it, and both close to it. */
function checkWorkoutMeals(day: ScheduleUIDay, pushTip: PushTip) {
  let workout = day.items.find(item => item.type === Schedule_ItemType.Workout);
  if (!workout) {
    return;
  }
  let preWoMeal = maxBy(
    day.items.filter(item => item.type === Schedule_ItemType.Meal && item.hourId < workout.hourId),
    'hourId',
  );
  let postWoMeal = minBy(
    day.items.filter(item => item.type === Schedule_ItemType.Meal && item.hourId > workout.hourId),
    'hourId',
  );

  if (!preWoMeal) {
    // if the day holds a workout and no meals at all, then there is no meal
    // after it either, and this list means "the items this tip is about",
    // so leave the absent one out instead of pushing an undefined the
    // Schedule Tips page would then read a name off
    pushTip(ScheduleTipId.MealBeforeWorkout, { day: day, items: postWoMeal ? [workout, postWoMeal] : [workout] });
  } else if (!postWoMeal) {
    pushTip(ScheduleTipId.MealAfterWorkout, { day: day, items: [workout, preWoMeal] });
    // note, we check for 1 hr before or 2 hr after _start_ of WO - that is effectively 1 hr before/after WO, as WO is about 1hr long usually
  } else if (itemDistance(preWoMeal, workout) > 1.01 && itemDistance(postWoMeal, workout) > 2.01) {
    pushTip(ScheduleTipId.OneMealCloseToWo, { day: day, items: [preWoMeal, workout, postWoMeal] });
  } else if (itemDistance(preWoMeal, postWoMeal) > 5.01) {
    pushTip(ScheduleTipId.PrePostWoMealDistance, { day: day, items: [preWoMeal, workout, postWoMeal] });
  }
}

/** Checks the night between this day's bedtime and the next day's wake-up. */
function checkSleepLength(day: ScheduleUIDay, tomorrow: ScheduleUIDay | undefined, pushTip: PushTip) {
  let sleep = day.items.find(item => item.type === Schedule_ItemType.DayEnd);
  if (!tomorrow || !sleep) {
    return;
  }
  let tomorrowWake = tomorrow.items.find(item => item.type === Schedule_ItemType.DayStart);
  if (tomorrowWake && itemDistance(sleep, tomorrowWake) < 7.99) {
    pushTip(ScheduleTipId.ShortSleep, { day: [day, tomorrow], items: [sleep, tomorrowWake] });
  }
}

/** Checks the time from the day's first meal to its last. */
function checkFeedingWindow(day: ScheduleUIDay, pushTip: PushTip) {
  let meals = mealsOf(day);
  let firstMeal = meals[0];
  let lastMeal = meals[meals.length - 1];
  if (firstMeal && lastMeal && itemDistance(firstMeal, lastMeal) < 7.99) {
    pushTip(ScheduleTipId.ShortFeedingWindow, { day: day, items: [firstMeal, lastMeal] });
  }
}

/** Checks that no meal follows the one before it by less than 3 hours. */
function checkMealSpacing(day: ScheduleUIDay, pushTip: PushTip) {
  let meals = mealsOf(day);
  for (let i = 0; i < meals.length - 1; i++) {
    let prev = meals[i];
    let next = meals[i + 1];

    if (hourIdToHour(next.hourId) - hourIdToHour(prev.hourId) < 2.99) {
      pushTip(ScheduleTipId.MinTimeBetweenMeals, { day: day, items: [prev, next] });
    }
  }
}

@Injectable({
  providedIn: 'root'
})
export class CoachingTipsService {

  constructor() { }

  /**
   * Analyzes schedule and returns warnings about suboptimal timing
   * @param scheduleDays Array of schedule days with items
   * @returns Object mapping each tip that fired to the days/items it applies to
   */
  analyzeScheduleWarnings(scheduleDays: ScheduleUIDay[]): ScheduleWarningsData {
    let warnings: ScheduleWarningsData = {}
    const pushTip: PushTip = (id, occurrence) => {
      (warnings[id] ??= []).push(occurrence);
    };

    // each slot's time on every day that has it, for the cross-day regularity checks below
    let slotTimes: { [key: string]: SlotTime[][] } = {};
    let updateTiming = (day: ScheduleUIDay, item: ScheduleUIItem, indexInDay: number) => {
      let slotsOfType = slotTimes[item.type] ??= [];
      (slotsOfType[indexInDay] ??= []).push({ day: day, hour: hourIdToHour(item.hourId) });
    }

    for (let dayIndex = 0; dayIndex < scheduleDays.length; dayIndex++) {
      let day = scheduleDays[dayIndex];
      let tomorrow = dayIndex + 1 < scheduleDays.length ? scheduleDays[dayIndex + 1] : undefined;

      checkWorkoutMeals(day, pushTip);
      checkSleepLength(day, tomorrow, pushTip);
      checkFeedingWindow(day, pushTip);
      checkMealSpacing(day, pushTip);

      // record this day's times for the cross-day regularity checks below
      let workout = day.items.find(item => item.type === Schedule_ItemType.Workout);
      if (workout) {
        updateTiming(day, workout, 0);
      }
      let wake = day.items.find(item => item.type === Schedule_ItemType.DayStart);
      if (wake) {
        updateTiming(day, wake, 0);
      }
      let sleep = day.items.find(item => item.type === Schedule_ItemType.DayEnd);
      if (sleep) {
        updateTiming(day, sleep, 0);
      }
      let meals = mealsOf(day);
      for (let i = 0; i < meals.length; i++) {
        updateTiming(day, meals[i], i);
      }
    }

    // callback used to check if timings are consistent across days
    let checkTiming = (itemType: Schedule_ItemType, i: number, inconsistentTip: ScheduleTipId) => {
      let times = slotTimes[itemType]?.[i];
      if (!times || times.length === 0) {
        return;
      }
      // measure on the clock, so 23:00 and 00:30 are 1.5 hours apart rather than 22.5
      let hours = unwrapHoursOnClock(times.map(time => time.hour));
      let spread_hr = Math.max(...hours) - Math.min(...hours);
      if (spread_hr > 2.01) {
        let name = this.getScheduleItemName(itemType, i);
        let avg_hr = hours.reduce((sum, hour) => sum + hour, 0) / hours.length;
        // list the days furthest from the average. the tolerance stops rounding in the sum
        // from deciding a tie, so the days chosen never depend on the order of the days
        let distances = hours.map(hour => Math.abs(hour - avg_hr));
        let maxDistance_hr = Math.max(...distances);
        let furthestDays = times.filter((_, k) => distances[k] >= maxDistance_hr - 1e-9).map(time => time.day);
        // the tip exists even when no day is listed, as it always has
        warnings[inconsistentTip] ??= [];
        for (let day of furthestDays) {
          pushTip(inconsistentTip, { day: day, items: [{ name: name, type: itemType, }] });
        }
      }
    }

    checkTiming(Schedule_ItemType.DayStart, 0, ScheduleTipId.SleepTimingIrregular);
    checkTiming(Schedule_ItemType.DayEnd, 0, ScheduleTipId.SleepTimingIrregular);
    checkTiming(Schedule_ItemType.Meal, 0, ScheduleTipId.MealTimingIrregular);
    checkTiming(Schedule_ItemType.Meal, 1, ScheduleTipId.MealTimingIrregular);
    checkTiming(Schedule_ItemType.Meal, 2, ScheduleTipId.MealTimingIrregular);
    checkTiming(Schedule_ItemType.Meal, 3, ScheduleTipId.MealTimingIrregular);
    checkTiming(Schedule_ItemType.Workout, 0, ScheduleTipId.WorkoutTimingIrregular);

    return warnings;
  }

  /**
   * Gets schedule warnings by analyzing the schedule configuration
   * @param schedule The schedule object with rules
   * @param startDate Start date for analysis
   * @param endDate End date for analysis
   * @returns Object mapping each tip that fired to the days/items it applies to
   */
  getScheduleWarnings(schedule: Schedule, startDate: DateTime, endDate: DateTime): ScheduleWarningsData {
    // generate schedule days from rules
    const generatedDays = generateScheduleDaysFromRules(schedule.rules, startDate, endDate);

    const scheduleDays: ScheduleUIDay[] = [];

    for (let dayIndex = 0; dayIndex < generatedDays.length; dayIndex++) {
      const day = generatedDays[dayIndex];

      // meals are named by their order within the day ("Meal 1", "Meal 2", ...),
      // so count them off as we walk the day's items, which the generator has
      // already sorted by time
      const items: ScheduleUIItem[] = [];
      let mealsSoFar = 0;
      for (const item of day.items) {
        // if this item is a meal, it takes the next meal number
        const mealIndex = item.type === Schedule_ItemType.Meal ? mealsSoFar : undefined;
        if (item.type === Schedule_ItemType.Meal) {
          mealsSoFar++;
        }
        items.push({
          name: this.getScheduleItemName(item.type, mealIndex),
          type: item.type,
          hourId: wrapIntegerToInterval(Math.round(item.time_hr * hourDivisions), 24 * hourDivisions),
          dayIndex: dayIndex,
          timeISO: hoursToIsoTime(item.time_hr)
        });
      }

      // the window starts on the caller's start date, which is not a Monday in
      // general, so the header has to name the day's REAL weekday
      const date = startDate.plus({ days: dayIndex });

      scheduleDays.push({
        index: dayIndex,
        headerText: weekdayName(date.weekday),
        items: items,
        indexOffset: 0
      });
    }

    // analyze schedule for warnings
    return this.analyzeScheduleWarnings(scheduleDays);
  }

  /**
   * Display name for a schedule item, used inside the timing-consistency warnings.
   *
   * `index` is the caller's 0-based slot number. Meals are NAMED 1-based
   * ("Meal 1" is the first meal — see `shared/src/data-model/schedule.ts`
   * `createMealEntry`), so it is shifted here. Before #4317 this read
   * `Meal ${index}` and reported "Meal 0" for the first meal.
   */
  getScheduleItemName = (type: Schedule_ItemType, index?: number) => {
    switch (type) {
      case Schedule_ItemType.Meal:
        // if a meal slot arrives without its index, that is a programmer error —
        // the old code threw here, and silently rendering "Meal undefined" to the
        // user would hide it
        if (index === undefined) {
          throw new Error('getScheduleItemName: a Meal item requires its index');
        }
        return LL.dayItems.titles.meal({ number: index + 1 });
      case Schedule_ItemType.Workout:
        return LL.coachingTips.scheduleItems.training();
      case Schedule_ItemType.DayStart:
        return LL.coachingTips.scheduleItems.wakeUp();
      case Schedule_ItemType.DayEnd:
        return LL.coachingTips.scheduleItems.goToSleep();
      case Schedule_ItemType.WeeklyCheckup:
        return LL.coachingTips.scheduleItems.weeklyCheckup();
      default: {
        // if a new schedule-item type is added and not handled above, this
        // assignment fails the build. A value read from a stored schedule can
        // still be anything at runtime, so it must not silently produce `undefined`
        const unhandled: never = type;
        throw new Error(`getScheduleItemName: unhandled schedule item type '${String(unhandled)}'`);
      }
    }
  };
}
